import { useState, useEffect } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { HandRaisedIcon, SparklesIcon, MapPinIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CalendarIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import EventSuggestionAPI from "../../api/EventSuggestionApi";
import { SuggestionCard } from "../EventSuggestions";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

// Live list of suggestions this host has claimed but not yet turned into events.
// A claim sets pickedUpBy = vendorID while status stays "pending"; once the host
// finishes (Create Event) the status flips to "approved" and it drops off here.
const useClaimedSuggestions = (vendorID) => {
  const [claimed, setClaimed] = useState([]);

  useEffect(() => {
    if (!vendorID) return;
    const unsubscribe = EventSuggestionAPI.subscribeClaimedBy(
      vendorID,
      (suggestions) => setClaimed(suggestions.filter((s) => s.status === "pending")),
      (err) => console.error("Error fetching claimed suggestions:", err)
    );
    return () => unsubscribe();
  }, [vendorID]);

  return claimed;
};

// "Finish creating these events" — claimed suggestions awaiting conversion.
// Gives a host one place on the Upcoming tab to finish turning the suggestions
// they picked up into real events. Clicking a card opens the full suggestion.
export const FinishCreatingSection = ({ vendorID, isSuperAdmin }) => {
  const claimed = useClaimedSuggestions(vendorID);
  const [selectedId, setSelectedId] = useState(null);

  // Drop the selection if that suggestion leaves the list (returned to the pool
  // or converted) so the preview doesn't linger over a stale doc.
  useEffect(() => {
    if (selectedId && !claimed.some((s) => s.id === selectedId)) setSelectedId(null);
  }, [claimed, selectedId]);

  const updateSuggestionStatus = async (suggestionId, newStatus) => {
    try {
      await EventSuggestionAPI.setStatus(suggestionId, newStatus);
    } catch (err) {
      console.error("Failed to update suggestion status:", err);
    }
  };

  if (claimed.length === 0) return null;

  const selected = claimed.find((s) => s.id === selectedId) || null;
  const one = claimed.length === 1;
  return (
    <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 dark:bg-primary/10 p-4">
      <div className="flex items-center gap-2">
        <HandRaisedIcon className="h-5 w-5 text-primary" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Finish creating these events</h2>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
          {claimed.length}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        You claimed {one ? "this idea" : "these ideas"}. Finish setting {one ? "it" : "them"} up to add {one ? "it" : "them"} to your events.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {claimed.map((s) => (
          <ClaimedSuggestionCard
            key={s.id}
            suggestion={s}
            onOpen={() => setSelectedId(s.id)}
          />
        ))}
      </div>

      <SuggestionPreviewModal
        suggestion={selected}
        onClose={() => setSelectedId(null)}
        onUpdateStatus={updateSuggestionStatus}
        isSuperAdmin={isSuperAdmin}
        vendorID={vendorID}
      />
    </div>
  );
};

// Compact card for a claimed-but-unconverted suggestion. Clicking it opens the
// full suggestion (which carries the Create Event / Return to pool actions).
const ClaimedSuggestionCard = ({ suggestion, onOpen }) => {
  const dateLabel = suggestion.suggestedDate
    ? suggestion.suggestedDate.toDate().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    : null;

  return (
    <div
      onClick={onOpen}
      className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-primary/40 shadow-sm cursor-pointer hover:shadow-md hover:border-primary/60 transition-all"
    >
      {suggestion.imageUrl ? (
        <img
          src={variantURL(suggestion.imageUrl, 400)}
          onError={fallbackToOriginal(suggestion.imageUrl)}
          alt={suggestion.eventName}
          className="h-16 w-16 rounded-md object-cover shrink-0"
        />
      ) : (
        <div className="h-16 w-16 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <SparklesIcon className="h-6 w-6 text-primary/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">
          {suggestion.eventName}
        </h3>
        <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          {(dateLabel || suggestion.suggestedTime) && (
            <div className="flex items-center gap-1">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
              <span className="truncate">
                {dateLabel}{suggestion.suggestedTime ? ` · ${suggestion.suggestedTime}` : ""}
              </span>
            </div>
          )}
          {suggestion.venue && (
            <div className="flex items-center gap-1">
              <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
              <span className="truncate">{suggestion.venue}</span>
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        className="shrink-0 inline-flex items-center gap-0.5 rounded-md bg-primary pl-2.5 pr-1.5 py-1.5 text-xs font-semibold text-white hover:bg-primaryDark"
      >
        Open
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

// Modal that shows the full claimed idea (reuses the Ideas-tab card,
// which carries its own actions: Create Event, Return to pool, etc.).
const SuggestionPreviewModal = ({ suggestion, onClose, onUpdateStatus, isSuperAdmin, vendorID }) => {
  if (!suggestion) return null;

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/50 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-gray-800 shadow-xl transition-all duration-200 data-[closed]:opacity-0 data-[closed]:scale-95"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                Event Idea
              </p>
              <button
                onClick={onClose}
                className="rounded-full p-1.5 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[75vh] overflow-y-auto">
              <SuggestionCard
                suggestion={suggestion}
                onUpdateStatus={onUpdateStatus}
                isSuperAdmin={isSuperAdmin}
                vendorID={vendorID}
                hosts={[]}
                usersById={{}}
                mapMode
                showMap
              />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
